import { Pagination } from "../types/pagination";
import { CostCentreApprovalRequestItem } from "../types/rental-cost-centre-approval";
import { IRentalCostCentreApprovalService } from "../types/services/rental-cost-centre-approval-service";
import { FetchResult, GET, POST } from "../utils/fetch";

type ApprovalsListResponse = {
  approvals: any[];
  paginate: Pagination;
};

export default class RentalCostCentreApprovalService
  implements IRentalCostCentreApprovalService {
  async getApprovalRequests(
    staff?: string,
    property?: string[],
    location?: string,
    status?: string,
    departments?: string[],
    search?: string,
    page?: string,
    page_size?: string,
  ): Promise<FetchResult<CostCentreApprovalRequestItem[]>> {
    const qp = new URLSearchParams();
    qp.set("approval_type", "cost_centre");

    if (staff) qp.set("staff_id", staff);
    if (search) qp.set("query", search);
    if (location) qp.set("location_id", location);
    if (status) qp.set("status", status);
    if (departments && departments.length) {
      departments.forEach((d) => qp.append("departments[]", d));
    }
    if (page) qp.set("page", page);
    if (page_size) qp.set("per_page", page_size);

    const marshalled = qp.toString();
    const url = `/api/v2/rental/approvals?${marshalled}`;

    const { error, response } = await GET<ApprovalsListResponse>(url);

    if (error || !response) {
      return {
        error: error || new Error("Unknown Error"),
        response: null,
      };
    }

    // Map response to items
    const items: CostCentreApprovalRequestItem[] = response.approvals.map(
      (item: any) => ({
        id: item.id,
        opportunity_slug: item.opportunity?.slug || "",
        property_name: item.reservation_details?.property_name || "",
        checkin_date: item.reservation_details?.check_in || "",
        checkout_date: item.reservation_details?.check_out || "",
        total_booking_amount: item.amount_details?.total_booking_amount || 0,
        cost_centre_amount: Number.parseFloat(item.budget_amount || "0"),
        department: item.department || "",
        poc_name: item.requested_by || "",
        requested_date: item.requested_at || item.created_at || "",
        authorizer_id: item.approver_id || -1,
        authorizer_name: item.approver_name || "",
        guest_name: item.reservation_details?.guest_name || "",
        status: item.status || "",
        type: item.type,
        charge_details: item.charge_details,
      }),
    );

    return {
      error: null,
      paginate: response.paginate,
      response: items,
    };
  }

  async approveRequest(id: number): Promise<FetchResult<void>> {
    const payload = {
      approval_type: "cost_centre",
    };
    return POST<void>(`/api/v2/rental/approvals/${id}/accept`, payload);
  }

  async rejectRequest(id: number): Promise<FetchResult<void>> {
    const payload = {
      approval_type: "cost_centre",
    };
    return POST<void>(`/api/v2/rental/approvals/${id}/reject`, payload);
  }
}
